import React from 'react';
import { SeriesStatus } from '../types';

export interface Explanation {
  heading: string;
  body: string;
  detail?: string;
}

/** What a chart plots, in the words its explanation needs.
 *
 *  Every surface behind the fan-separation gate goes blank for the same four
 *  reasons but measures a different thing, so the reason codes are shared and
 *  the nouns are not. Passing the noun in keeps one code path without any chart
 *  claiming to show another's measurement.
 */
export interface ChartCopy {
  /** One-line "what this chart shows", used verbatim in the explanation. */
  description: string;
  /** One datum, as a noun: 'transition', 'run cycle'. */
  measurement: string;
  /** Plural of `measurement`, when it is not simply +s. */
  measurementPlural?: string;
}

/** Copy for a chart with nothing to plot.
 *
 *  Lives client-side so wording changes don't need a Lambda deploy; the server
 *  supplies the reason code and the numbers behind it. Callers pass already
 *  extracted primitives rather than the payload, so this stays free of any
 *  particular metric's field names.
 *
 *  There is deliberately no magnitude filter anywhere in this path. The on/off
 *  threshold upstream already decides what counts as a transition; the sensor's
 *  differential across it is then the measurement at whatever size it comes in.
 *  A 10 Pa floor used to sit here and in the pipeline, and it censored the
 *  return side roughly five times more often than the fan side.
 */
export const explainEmpty = (
  status: SeriesStatus | undefined,
  copy: ChartCopy
): Explanation | null => {
  const one = copy.measurement;
  const many = copy.measurementPlural ?? `${copy.measurement}s`;

  if (!status) return null; // Older payload — preserve the previous behaviour.

  const sep = status.observed_separation_pa;
  const days = status.days_evaluated;
  const dayWord = days === 1 ? 'day' : 'days';

  switch (status.reason) {
    case 'no_fan_off_state':
      return {
        heading: 'Not available for this system',
        body:
          `${copy.description} Over the last ${days} ${dayWord} this system's pressure ` +
          `stayed within ${sep} Pa, with no fan-off state recorded, so there are no ` +
          `${many} to measure.`,
        detail:
          'This is most often seen where the blower runs continuously. The chart ' +
          'populates on its own for any day the system cycles off.',
      };
    case 'insufficient_clusters':
      return {
        heading: 'Not available for this system',
        body:
          `Over the last ${days} ${dayWord} this system's pressure readings stayed at a ` +
          `single level, so there are no on/off ${many} to measure.`,
        detail:
          'This is most often seen where the blower runs continuously. The chart ' +
          'populates on its own for any day the system cycles off.',
      };
    case 'no_data':
      return {
        heading: 'No data yet',
        body: 'This device has no processed days in the selected range.',
      };
    case 'stale_calculation':
      // Not a property of the system — a property of how these days were
      // processed. The paired chart has values for the same days, which is what
      // proves the transitions were there to begin with.
      return {
        heading: 'Recorded, then discarded',
        body:
          `${copy.description} The system cycled on these days and the ${many} were ` +
          'detected, but an earlier version of the analysis discarded any whose ' +
          'pressure change was small — which removed every one on this side.',
        detail:
          'That filter has been removed, so days recorded since are unaffected. ' +
          'Earlier days fill in when reprocessed — those before 2026-03-15 need ' +
          'their raw readings restored from the archive first.',
      };
    case 'unavailable':
    default:
      // Deliberately vague: at least one suppression path is not recorded, so
      // naming a cause here would be inventing one.
      return {
        heading: 'Not available',
        body: `No ${one} data was recorded for the last ${days} ${dayWord}.`,
      };
  }
};

export interface SeriesExplanationInput {
  label: string;
  status: SeriesStatus | undefined;
}

/** Resolve one explanation for a whole overlay.
 *
 *  Series can be empty for different reasons, and averaging them into a single
 *  confident sentence would assert something untrue of at least one device. So:
 *  a single series uses its own copy; several series that agree share it; and
 *  series that disagree get a neutral summary naming each one, rather than a
 *  cause picked arbitrarily from the first.
 */
export const explainEmptyAcross = (
  entries: SeriesExplanationInput[],
  copy: ChartCopy
): Explanation | null => {
  const explanations = entries.map((e) => explainEmpty(e.status, copy));
  if (explanations.some((e) => e === null)) return null;

  const resolved = explanations as Explanation[];
  if (resolved.length === 1) return resolved[0];

  const allAgree = resolved.every((e) => e.body === resolved[0].body);
  if (allAgree) return resolved[0];

  return {
    heading: 'Not available for the selected analyses',
    body: `None of the ${resolved.length} selected series recorded transitions to plot.`,
    detail: entries.map((e, i) => `${e.label}: ${resolved[i].heading.toLowerCase()}`).join(' · '),
  };
};

/** Replaces the plot while keeping the chart's title, so an absent series reads
 *  as absent rather than broken. */
export const ChartUnavailable: React.FC<Explanation> = ({ heading, body, detail }) => (
  <div
    className="flex items-center justify-center rounded-sm border border-dashed border-border bg-gray-50 px-6 py-10"
    style={{ minHeight: '160px' }}
    data-testid="chart-unavailable"
  >
    <div className="max-w-xl text-center">
      <p className="text-sm font-medium text-gray-700">{heading}</p>
      <p className="mt-2 text-sm text-gray-600">{body}</p>
      {detail && <p className="mt-2 text-xs text-muted-foreground">{detail}</p>}
    </div>
  </div>
);
